import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import Swal from 'sweetalert2';
import '../PlayingPlan/NewPlan.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const NewPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const planIdFromQuery = searchParams.get('planId');
  const planId = location.state?.planId || planIdFromQuery;
  const [selectedSports, setSelectedSports] = useState([])
  const [selectedSport, setSport] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [sportOptions, setSportOptions] = useState([])
  const [selectedFileName, setSelectedFileName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [tier, setTier] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [benefitsTable, setBenefitsTable] = useState([
    { name: '', icon: null, iconPreview: null }
  ]);
  const [features, setFeatures] = useState({
    allcentresAccess: false,
    tierwiseAccess: false,
    singletierAccess: false,
    recurringBooking: false,
  });

  const handleTierChange = opt => setTier(opt);
  const handlePlanTypeChange = opt => { setPlanType(opt); setSport(null); };
  const onFeatureToggle = key => e => setFeatures(f => ({ ...f, [key]: e.target.checked }));
  const [detailRows, setDetailRows] = useState([
    {
      validityMonth: '',
      courts: '',
      totalHours: '',
      advanceBookingDays: '',
      benefitType: null,
      benefitHours: '',
      shop: '',
      price: '',
      strikePrice: '',
      playingHours: {},
      detailSport: null,
      gstInclusive: false,
      extensionDays: '',
    }
  ]);



  const planTypeOptions = [
    { value: 'single', label: 'Single Sport' },
    { value: 'multi', label: 'Multi Sport' },
  ];

  useEffect(() => {
    const fetchPlanByName = async () => {
      if (!planId) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_ADMIN_PLAYING_PLAN_LIST}?id=${planId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            },
          }
        );

        const json = await response.json();
        if (!json.success) {
          throw new Error(json.success_message || 'Failed to fetch plan');
        }

        const planData = json.data.plans?.[0];
        if (!planData) {
          throw new Error('No plan data found for the given name');
        }

        const foundTier = tierOptions.find(t => t.value === planData.name);
        if (foundTier) {
          setTier(foundTier);
        } else {
          setTier({ label: planData.name, value: planData.name });
        }

        setPlanType({
          label: planData.type === 'SINGLE' ? 'Single Sport' : 'Multi Sport',
          value: planData.type.toLowerCase(),
        });

        setFeatures(prev => ({
          ...prev,
          allcentresAccess: !!planData.allCentreAccess,
          tierwiseAccess: !!planData.tierWise,
          singletierAccess: !!planData.singleTier,
          recurringBooking: !!planData.recurringBooking,
        }));

        setFormValues(prev => ({ ...prev, tagLine: planData.tagline || '' }));

        if (planData.planIconUrl) {
          setImagePreview(planData.planIconUrl);

          const iconUrlParts = planData.planIconUrl.split('/');
          const iconFileName = iconUrlParts[iconUrlParts.length - 1];
          setSelectedFileName(iconFileName || 'Existing Icon');
        }


        if (Array.isArray(planData.benefits) && planData.benefits.length > 0) {
          const prefilledBenefits = planData.benefits.map(b => {
            const benefitUrl = b.icon || '';
            const iconParts = benefitUrl.split('/');
            const iconName = iconParts[iconParts.length - 1];
            return {
              id: b.id,
              name: b.name || '',
              icon: null,
              iconPreview: benefitUrl || null,
              iconName: iconName || 'icon.png',
            };

          });

          setBenefitsTable(prefilledBenefits);
        }


        const blocks = planData.validityBlocks || [];

        if (planData.type === 'SINGLE') {
          const blockSportId = blocks[0]?.sportId;
          const sportOpt = sportOptions.find(
            s => String(s.value) === String(blockSportId)
          );
          if (sportOpt) setSport(sportOpt);
        } else if (planData.type === 'MULTI') {
          const sportsUsed = blocks.flatMap(b => b.sports || []).map(s => s.sportId);
          const uniqueSports = [...new Set(sportsUsed)].map(id =>
            sportOptions.find(s => String(s.value) === String(id))
          ).filter(Boolean);
          setSelectedSports(uniqueSports);
        }

        const rows = blocks.map(block => ({
          planId: block.planId || null,
          validityMonth: String(block.validityInMonths),
          courts: String(block.maxSlotBookingPerDay),
          totalHours: planData.type === 'SINGLE' ? String(block.totalPlayingHours || '') : '',
          advanceBookingDays: String(block.advanceBookingEligibilityDays),
          shop: String(block.shopRewards),
          benefitHours: String(block.complimentaryHours || ''),
          price: String(block.price || ''),
          strikePrice: String(block.strikeThroughPrice || ''),
          gstInclusive: block.gstInclusive,
          extensionDays: block.extensionDays !== null ? String(block.extensionDays) : '0',
          benefits: benefitOptions.find(opt => opt.value.toUpperCase() === (block.complementaryMembership || '').toUpperCase()) || null,
          detailSport: sportOptions.find(opt => String(opt.value) === String(block.complementarySportId)) || null,
          playingHours:
            planData.type === 'MULTI' && block.sports
              ? block.sports.reduce((acc, s) => {
                acc[s.sportId] = String(s.playingHours || '');
                return acc;
              }, {})
              : {},
        }));

        setDetailRows(rows);

        const vb = blocks.map(block => ({
          validityMonth: String(block.validityInMonths),
        }));
        setValidityBlocks(vb);

      } catch (error) {
        console.error('Edit fetch error:', error);
        Swal.fire({
          icon: 'error',
          title: 'Fetch Failed',
          text: error.message || 'Unable to load plan data.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    };

    fetchPlanByName();
  }, [planId, sportOptions]);



  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(process.env.REACT_APP_API_TIER_FETCH, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        });
        const json = await res.json();

        if (json.success) {
          const sportsObj = json.data.sports || {};
          const sports = Object.entries(sportsObj).map(([id, name]) => ({
            value: id,
            label: name,
          }));
          setSportOptions(sports);
        } else {
          console.error('API returned success:false', json);
        }
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };
    fetchData();
  }, []);
  const handleBenefitChange = (idx, field) => e => {
    const value = field === 'icon' ? e.target.files[0] : e.target.value;
    setBenefitsTable(prev =>
      prev.map((row, i) =>
        i === idx
          ? {
            ...row,
            [field]: value,
            iconPreview:
              field === 'icon' && value ? URL.createObjectURL(value) : row.iconPreview
          }
          : row
      )
    );
  };

  const addBenefitRow = () => {
    if (benefitsTable.length >= 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Limit Reached',
        text: 'You can add a maximum of 6 benefits.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    setBenefitsTable(prev => [...prev, { name: '', icon: null, iconPreview: null }]);
  };

  const removeBenefitRow = (idx) => {
    setBenefitsTable(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };


  const tierOptions = [
    { value: 'elite', label: 'Elite' },
    { value: 'premium', label: 'Premium' },
    { value: 'standard', label: 'Standard' },
  ];

  const [formValues, setFormValues] = useState({
    name: '', planType: '', price: '', strikethroughPrice: '',
    gstInclusive: '', tagLine: '', playingHours: '', tagLine: '',
    maxSlotBooking: '', coinsRewarded: '', planIcon: null,
  });

  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      alert("Only image files are allowed!"); return;
    }
    setSelectedFileName(file.name);
    setFormValues(v => ({ ...v, planIcon: file }));
    setImagePreview(URL.createObjectURL(file));
  };


  const addDetailRow = () => setDetailRows(rows => [
    ...rows,
    {
      validityMonth: '',
      courts: '',
      totalHours: '',
      advanceBookingDays: '',
      benefitType: null,
      benefitHours: '',
      shop: '',
      price: '',
      strikePrice: '',
      playingHours: {},
      detailSport: null,
      gstInclusive: false,
      extensionDays: '',
    }
  ]);

  const handleDetailSportChange = (rowIdx) => (selected) => {
    setDetailRows(rows =>
      rows.map((r, i) =>
        i === rowIdx ? { ...r, detailSport: selected } : r
      )
    );
  };



  const handlePlayingHoursChange = (rowIdx, sportValue) => e => {
    const v = e.target.value;
    if (/^[0-9]*\.?[0-9]*$/.test(v)) {
      setDetailRows(rows =>
        rows.map((r, i) =>
          i === rowIdx
            ? {
              ...r,
              playingHours: {
                ...r.playingHours,
                [sportValue]: v
              }
            }
            : r
        )
      );
    }
  };

  const benefitOptions = [
    { value: 'Coaching', label: 'Coaching' },
    { value: 'Playing', label: 'Playing' },

  ];

  const handleShopChange = rowIdx => e => {
    const v = e.target.value;
    if (!/\r|\n/.test(v)) {
      setDetailRows(rows =>
        rows.map((r, i) =>
          i === rowIdx
            ? { ...r, shop: v }
            : r
        )
      );
    }
  };

  const handleBenefitsChange = (rowIdx) => (selected) => {
    setDetailRows(rows =>
      rows.map((r, i) =>
        i === rowIdx
          ? { ...r, benefits: selected || null }
          : r
      )
    );
  };

  const removeDetailRow = idx => setDetailRows(r => r.length > 1 ? r.filter((_, i) => i !== idx) : r);
  const handleDetailChange = (idx, field) => e => {
    const v = e.target.value;
    if (v === '' || /^[0-9]*\.?[0-9]*$/.test(v)) {
      setDetailRows(r => r.map((row, i) => i === idx ? { ...row, [field]: v } : row));
    }
  };

  const [validityBlocks, setValidityBlocks] = useState(
    detailRows.map(() => ({
      pricingRows: [{ centres: [], price: '', strikePrice: '' }]
    }))
  );

  useEffect(() => {
    setValidityBlocks(prev =>
      detailRows.map((row, idx) => ({
        validityMonth: row.validityMonth,
        pricingRows: prev[idx]?.pricingRows || [{ centres: [], price: '', strikePrice: '' }]
      }))
    );
  }, [detailRows]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (!tier || !planType || (planType.value === "single" && !selectedSport)) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text:
          !tier
            ? "Please select a Plan Name."
            : !planType
              ? "Please select a Plan Type."
              : "Please choose a Sport for your Single‐Sport plan.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setIsSubmitting(false);
      return;
    }

    const isEditMode = !!planId;

    if (!isEditMode) {
      const benefitIconMissing = benefitsTable.some(b => b.name && !b.icon);
      if (benefitIconMissing) {
        setIsSubmitting(false);
        return Swal.fire({
          icon: "error",
          title: "Missing Icon",
          text: "Every benefit with a name must have an icon uploaded.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }

    if (!isEditMode && !formValues.planIcon) {
      setIsSubmitting(false);
      return Swal.fire({
        icon: "error",
        title: "Plan Icon Missing",
        text: "Please upload a Plan Icon before submitting.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
    const isSingle = planType?.value === "single";
    const payload = {
      id: planId || null,
      sportId: isSingle ? selectedSport?.value ?? null : 0,
      name: tier.label,
      type: planType.value.toUpperCase(),
      allCentreAccess: features.allcentresAccess,
      tierWise: features.tierwiseAccess,
      singleTier: features.singletierAccess,
      planBenefitsId: tier.benefitsId || tier.valueId,
      tagline: formValues.tagLine,
      recurringBooking: features.recurringBooking,
      validityBlocks: detailRows.map((row) => {
        const block = {
          planId: row.planId || null,
          validityInMonths: Number(row.validityMonth),
          advanceBookingEligibilityDays: Number(row.advanceBookingDays),
          maxSlotBookingPerDay: Number(row.courts),
          shopRewards: Number(row.shop),
          complimentaryHours: Number(row.benefitHours),
          complementaryMembership: row.benefits?.value || null,
          complementarySportId: row.detailSport?.value || null,
          price: String(row.price),
          strikeThroughPrice: String(row.strikePrice),
          extensionDays: Number(row.extensionDays) || 0,
          gstInclusive: row.gstInclusive,

        };

        if (planType.value === "single") {
          block.sportId = selectedSport.value;
          block.totalPlayingHours = Number(row.totalHours);
        } else {
          block.sports = selectedSports.map((s) => ({
            sportId: s.value,
            playingHours: Number(row.playingHours[s.value] || 0),
          }));
        }

        return block;
      }),
      benefits: benefitsTable.map((b) => ({
        name: b.name,
        ...(isEditMode && {
          id: b.id,
          icon: b.iconPreview
        }),
      })),
    };

    const formData = new FormData();
    formData.append("form", new Blob([JSON.stringify(payload)], { type: "application/json" }));

    if (formValues.planIcon) {
      formData.append("planIcon", formValues.planIcon);
    }

    benefitsTable.forEach((benefit) => {
      if (benefit.icon) {
        formData.append("benefitIcons", benefit.icon);
      }
    });

    try {
      const response = await fetch(process.env.REACT_APP_API_ADMIN_CREATE_PLAYING_PLAN, {
        method: "POST",
        headers: {
          "x-authorization": process.env.REACT_APP_AUTH_TOKEN,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMsg = "Something went wrong while submitting the form.";
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMsg = errorData.message;
          } else if (errorData?.errors) {
            errorMsg = Object.values(errorData.errors).flat().join("\n");
          } else if (typeof errorData === "string") {
            errorMsg = errorData;
          }
        } catch (jsonError) {
          errorMsg = `Unexpected error occurred. Status: ${response.status}`;
        }
        setIsSubmitting(false);
        return Swal.fire({
          icon: "error",
          title: "Submission Failed!",
          text: errorMsg,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 4000,
        });
        setIsSubmitting(false);
      }

      const responseData = await response.json();
      const msg = responseData?.success_message || "Plan created successfully.";
      navigate(-1);

      return Swal.fire({
        icon: "success",
        title: "Success!",
        text: msg,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setIsSubmitting(false);
    } catch (error) {
      return Swal.fire({
        icon: "error",
        title: "Network Error!",
        text:
          error?.message ||
          "Something went wrong. Please check your internet connection.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };




  const handleMultiSportChange = opts => {
    const sel = opts || [];
    setSelectedSports(sel);
    setDetailRows(rows =>
      rows.map(row => {
        const ph = {};
        sel.forEach(({ value }) => {
          ph[value] = row.playingHours?.[value] || '';
        });
        return { ...row, playingHours: ph };
      })
    );
  };
  const isBlocked = !!planId;
  const anyIconUploaded = benefitsTable.some(b => b.iconPreview);

  return (
    <form onSubmit={handleSubmit}>
      <div className="enrollment-container">
        <div className="breadcrumb">
          <Link to="/planList" className="breadcrumb-link">Plan List</Link> &gt; New Plan
        </div>

        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="tier"
            inputId="tier-select"
            options={tierOptions}
            value={tier}
            onChange={handleTierChange}
            placeholder="Select Plan"
            className={`walkin-form-dropdown ${tier ? "has-value" : ""}`}
            classNamePrefix="walkin-select"
            isClearable={!isBlocked}
            isSearchable={!isBlocked}
            menuIsOpen={isBlocked ? false : undefined}
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />

          {tier && <label htmlFor="tier-select">Plan Name</label>}
        </div>


        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="planType"
            inputId="plan-type"
            options={planTypeOptions}
            value={planType}
            isClearable={!isBlocked}
            menuIsOpen={isBlocked ? false : undefined}
            isSearchable={!isBlocked}
            onChange={handlePlanTypeChange}
            placeholder="Select Plan Type"
            className={`walkin-form-dropdown ${planType ? "has-value" : ""}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '5px',
              }),
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />

          {planType && <label htmlFor="plan-type">Plan Type</label>}
        </div>

        {planType?.value === 'single' ? (
          <div className="floating-select-form" style={{ width: '100%' }}>
            <Select
              name="sport"
              inputId="sport-select"
              options={sportOptions}
              value={selectedSport}
              onChange={setSport}
              placeholder="Select one sport…"
              className={`walkin-form-dropdown ${selectedSport ? "has-value" : ""}`}
              classNamePrefix="walkin-select"
              isClearable
              styles={{
                control: (provided) => ({
                  ...provided,
                  padding: '5px',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
              }}
            />

            {selectedSport && <label htmlFor="sport-select">Sport</label>}
          </div>

        ) : planType?.value === 'multi' ? (
          <div className="floating-select-form" style={{ width: '100%' }}>
            <Select
              options={sportOptions}
              isMulti
              closeMenuOnSelect={false}
              value={selectedSports}
              onChange={handleMultiSportChange}
              placeholder="Pick sports…"
              className={`walkin-form-dropdown ${selectedSports.length ? "has-value" : ""}`}
              classNamePrefix="walkin-select"
              styles={{
                control: prov => ({ ...prov, padding: '5px' }),
                placeholder: prov => ({ ...prov, color: 'black' }),
              }}
            />
            {selectedSports.length > 0 && <label>Sports</label>}
          </div>
        ) : null}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <input
            type="text"
            name="tagLine"
            className={`walkin-form ${formValues.tagLine ? "has-value" : ""}`}
            value={formValues.tagLine}
            onChange={e => setFormValues(v => ({ ...v, tagLine: e.target.value }))}
            placeholder="Enter Tagline"
            required
          />
          {formValues.tagLine && <label htmlFor="tagLine">Plan Tagline</label>}
        </div>
        <div style={{ overflow: 'scroll' }}>
          <fieldset className="enrollment-form-fieldset">
            <legend className="enrollment-form-legend">Plan Details</legend>
            <table className="rental-entry-table">
              <thead>
                <tr>
                  <th>Validity</th>
                  <th>No of Courts</th>
                  {planType?.value === 'single' && (
                    <th>Total playing hours</th>
                  )}
                  <th benefitType>Price</th>
                  <th >Strike Price</th>
                  <th >Advance booking eligibility</th>
                  <th >Shop Rewards</th>
                  <th>GST Applicable</th>
                  <th>Extension Days</th>

                  <th >Complimentary Hours</th>
                  <th >Complimentary Sports</th>
                  <th >Complimentary Benefit Type</th>

                  {planType?.value === 'multi' && selectedSports.map(s => (
                    <th key={s.value} >{s.label} Hours</th>
                  ))}

                  <th >Action</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, idx) => (
                  <tr key={idx}>
                    <td >
                      <input
                        type="number" min="0"
                        step="any"
                        value={row.validityMonth}
                        onChange={handleDetailChange(idx, 'validityMonth')}
                        className="enrollment-form-input rental-input"
                        placeholder='0 month'
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number" min="0"
                        step="any"
                        value={row.courts}
                        onChange={handleDetailChange(idx, 'courts')}
                        className="enrollment-form-input rental-input"
                        placeholder='1 time slot'
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>

                    {planType?.value === 'single' && (
                      <td>
                        <input
                          type="number" min="0"
                          step="any"
                          value={row.totalHours}
                          onChange={handleDetailChange(idx, 'totalHours')}
                          className="enrollment-form-input rental-input"
                          placeholder='0 hours'
                          onWheel={e => e.target.blur()}
                          onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                          style={{
                            width: '120px',
                          }}
                        />
                      </td>
                    )}
                    <td>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.price}
                        onChange={handleDetailChange(idx, 'price')}
                        className="enrollment-form-input rental-input"
                        placeholder="₹ 0"
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={row.strikePrice}
                        onChange={handleDetailChange(idx, 'strikePrice')}
                        className="enrollment-form-input rental-input"
                        placeholder="₹ 0"
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number" min="0"
                        step="any"
                        value={row.advanceBookingDays}
                        onChange={handleDetailChange(idx, 'advanceBookingDays')}
                        className="enrollment-form-input rental-input"
                        placeholder='0 days'
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number" min="0"
                        step="any"
                        value={row.shop}
                        onChange={handleShopChange(idx)}
                        className="enrollment-form-input rental-input"
                        placeholder='0 rewards'
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>
                    <td style={{ paddingTop: '30px' }}>
                      <Select
                        options={[
                          { value: true, label: 'Yes' },
                          { value: false, label: 'No' }
                        ]}
                        value={row.gstInclusive !== undefined ?
                          { value: row.gstInclusive, label: row.gstInclusive ? 'Yes' : 'No' } :
                          null
                        }
                        onChange={(selected) => {
                          const value = selected ? selected.value : false;
                          setDetailRows(rows =>
                            rows.map((r, i) =>
                              i === idx ? { ...r, gstInclusive: value } : r
                            )
                          );
                        }}
                        placeholder="Select"
                        className="enrollment-form-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({ ...base, minWidth: 120 }),
                        }}
                      />
                    </td>

                    <td>
                      <input
                        type="number" min="0"
                        step="1"
                        value={row.extensionDays}
                        onChange={handleDetailChange(idx, 'extensionDays')}
                        className="enrollment-form-input rental-input"
                        placeholder="0"
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{ width: '120px' }}
                      />
                    </td>

                    <td>
                      <input
                        type="number" min="0"
                        step="any"
                        value={row.benefitHours}
                        onChange={handleDetailChange(idx, 'benefitHours')}
                        className="enrollment-form-input rental-input"
                        placeholder="0"
                        onWheel={e => e.target.blur()}
                        onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                        style={{
                          width: '120px',
                        }}
                      />
                    </td>
                    <td style={{ paddingTop: '30px' }}>
                      <Select
                        options={sportOptions}
                        value={row.detailSport}
                        onChange={handleDetailSportChange(idx)}
                        placeholder="Select Sport"
                        className="enrollment-form-select"
                        menuPortalTarget={document.body}
                        isClearable
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({ ...base, minWidth: 160 }),
                        }}
                      />
                    </td>

                    <td style={{ paddingTop: '30px' }}>
                      <Select
                        options={benefitOptions}
                        value={row.benefits}
                        placeholder="Select Benefit Type"
                        onChange={handleBenefitsChange(idx)}
                        isClearable
                        className="enrollment-form-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          singleValue: base => ({
                            ...base,
                            whiteSpace: 'normal',
                            overflow: 'visible',
                          }),
                          control: base => ({
                            ...base,
                            minWidth: 200,
                          })
                        }}
                      />
                    </td>


                    {planType?.value === 'multi' && selectedSports.map(s => (
                      <td key={s.value}>
                        <input
                          type="number" min="0"
                          step="any"
                          value={row.playingHours[s.value] || ''}
                          onChange={handlePlayingHoursChange(idx, s.value)}
                          className="enrollment-form-input rental-input"
                          placeholder="0"
                          onWheel={e => e.target.blur()}
                          onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                          style={{
                            width: '120px',
                          }}
                        />
                      </td>
                    ))}

                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="enrollment-form-button enrollment-form-button-danger"
                        onClick={() => removeDetailRow(idx)}
                        disabled={detailRows.length === 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

            </table>

            <button
              type="button"
              className="plusButton"
              onClick={addDetailRow}
              title="Add Plan Detail"
            >
              <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <g>
                  <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                </g>
              </svg>
            </button>

          </fieldset>
        </div>

        <div style={{ overflow: 'scroll' }}>
          <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
            <legend className="enrollment-form-legend">Plan Benefits</legend>
            <table className="rental-entry-table">
              <thead>
                <tr>
                  <th>Benefit Name</th>
                  <th>Icon</th>
                  {anyIconUploaded && <th>Preview</th>}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {benefitsTable.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        type="text"
                        className="rental-input"
                        value={row.name}
                        onChange={handleBenefitChange(idx, 'name')}
                        placeholder="Enter benefit name"
                        maxLength={25}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <input
                          type="file"
                          id={`benefit-icon-${idx}`}
                          accept="image/*"
                          style={{ display: 'none' }}
                          className="rental-input"
                          onChange={handleBenefitChange(idx, 'icon')}
                        />
                        <button
                          type="button"
                          style={{ padding: '4px 10px', border: 'none', background: '#eaf4ff' }}
                          onClick={() => document.getElementById(`benefit-icon-${idx}`).click()}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            id="upload-image"
                          >
                            <path
                              fill="black"
                              d="M19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h7a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21ZM22.71,4.29l-3-3a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-3,3a1,1,0,0,0,1.42,1.42L18,4.41V10a1,1,0,0,0,2,0V4.41l1.29,1.3a1,1,0,0,0,1.42,0A1,1,0,0,0,22.71,4.29Z"
                            />
                          </svg>
                        </button>

                        {row.icon && (
                          <span style={{ fontSize: '12px', marginTop: '4px' }}>
                            {row.icon.name}
                          </span>
                        )}
                      </div>
                    </td>

                    {anyIconUploaded && (
                      <td>
                        <img
                          src={row.iconPreview}
                          alt="Icon Preview"
                          style={{ width: 40, height: 40 }}
                        />
                      </td>
                    )}


                    <td>
                      <button
                        type="button"
                        className="enrollment-form-button enrollment-form-button-danger"
                        onClick={() => removeBenefitRow(idx)}
                        disabled={benefitsTable.length === 1}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button type="button" className="plusButton" onClick={addBenefitRow} title="Add Benefit">
              <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <g>
                  <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                </g>
              </svg>
            </button>

          </fieldset>
        </div>

        <fieldset className="enrollment-form-fieldset enrollment-form-mt-3">
          <legend className="enrollment-form-legend">Additional Options</legend>
          <div className="additional-options">
            {Object.entries(features).map(([key]) => (
              <div key={key} className="option-item enrollment-form-mt-1">
                <label>
                  <input
                    type="checkbox"
                    checked={features[key]}
                    onChange={onFeatureToggle(key)}
                  />{' '}
                  {{
                    allcentresAccess: 'All Centres Access',
                    tierwiseAccess: 'Tier Wise Access',
                    singletierAccess: 'Single Tier Access',
                    recurringBooking: 'Recurring Booking',
                  }[key]}
                </label>
              </div>
            ))}

          </div>
        </fieldset>

        {/* Plan Icon */}
        <div
          className="enrollment-form-section"
          style={{
            padding: '10px',
            margin: '0px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <label
            className="enrollment-form-label"
            style={{
              margin: '0px',
              marginTop: '10px',
              color: 'black',
              fontWeight: 500
            }}
          >
            Plan Icon
          </label>

          <div
            className="enrollment-form-image-preview"
            style={{
              width: '120px',
              height: '120px',
              overflow: 'hidden',
              borderRadius: '8px',
              border: '2px dashed #ccc',
              marginTop: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fafafa'
            }}
          >
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Plan Icon"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <span style={{ color: '#999', fontSize: '12px' }}>
                No Image
              </span>
            )}
          </div>

          <input
            type="file"
            id="fileInput"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
          />

          <button
            type="button"
            className="uploadbutton"
            style={{
              margin: '10px 0 0 0',
              padding: '6px 12px',
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <span className="uploadbutton__icon">
              <svg
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                height="24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fill="none" d="M0 0h24v24H0z" stroke="none"></path>
                <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
                <path d="M9 15l3 -3l3 3"></path>
                <path d="M12 12l0 9"></path>
              </svg>
            </span>
            <span className="uploadbutton__text">Upload</span>
          </button>

          {selectedFileName && (
            <span style={{ marginLeft: '0', marginTop: '4px', fontSize: '14px' }}>
              {selectedFileName}
            </span>
          )}
        </div>


        {/* Submit */}
        <div className="enrollment-form-section enrollment-form-mt-3">
          <button
            className="submit-button"
            type="submit"
            disabled={isSubmitting}
            style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Submitting...' : isBlocked ? 'Update Plan' : 'Create Plan'}
          </button>


        </div>
      </div>
    </form>
  )
}

export default NewPlan
